import axios from "axios";
import { getVueApp, getCurrentUser } from "@/services/shared";
import { redirectIfAuthExpired } from "@/services/security";
import { logEvent } from "@/services/analytic";
import {
  CLIENT_TIMEOUT,
  SERVER_TIMEOUT,
  CLIENT_ERROR,
  SERVER_ERROR,
  UNKNOWN_ERROR
} from "@/services/constants";

// Fix issue with IE11 caching previous queries
// https://github.com/axios/axios/issues/297#issuecomment-473229366
axios.defaults.headers.get.Pragma = "no-cache";
axios.defaults.headers.get["Cache-Control"] = "no-cache, no-store";

const _axios = axios.create({
  baseURL: "/services",
  // 2 mins timeout for non dev env
  timeout: process.env.NODE_ENV === "development" ? 999999999 : 120000
});

const logError = error => {
  //if response URL comes from analytics, were going to end up in an endless loop
  if (error.request.responseURL.includes(`/analytic`)) {
    return;
  }
  const errorDetails = {
    status: error.response ? error.response.status : null,
    statusText: error.response ? error.response.statusText : null,
    responseData: error.response ? error.response.data : null,
    message: error.message,
    // the errorType is mostly for other part of client side logic to use, not for dev debugging since we can pretty much tell the type of error from message or status
    errorType:
      !error.response && /timeout of \d+ms exceeded/.test(error.message)
        ? CLIENT_TIMEOUT
        : error.response && error.response.status === 504
        ? SERVER_TIMEOUT
        : error.response &&
          error.response.status >= 500 &&
          error.response.status < 600
        ? SERVER_ERROR
        : error.response &&
          error.response.status >= 400 &&
          error.response.status < 500
        ? CLIENT_ERROR
        : UNKNOWN_ERROR
  };
  const pageDetails = {
    location: location.href
  };
  const userDetails = getCurrentUser();

  logEvent("UI Error", {
    ErrorDetails: JSON.stringify(errorDetails),
    UserDetails: JSON.stringify(userDetails),
    PageDetails: JSON.stringify(pageDetails)
  });

  // for other part of app to differentiate the error
  error.vpErrorDetails = errorDetails;
};

_axios.interceptors.response.use(
  response => {
    if (getVueApp() && getVueApp().$route)
      redirectIfAuthExpired(getVueApp().$route);
    return response;
  },
  error => {
    if (getVueApp() && getVueApp().$route)
      redirectIfAuthExpired(getVueApp().$route);
    logError(error);
    return Promise.reject(error);
  }
);

export const get = (APIorUrl, params = {}) => {
  const url = APIorUrl.path || APIorUrl;
  return _axios.get(url, { params });
};

export const post = (APIorUrl, data = {}, config = {}) => {
  const url = APIorUrl.path || APIorUrl;

  // deflaten it
  if (!config.headers && !config.params) config = { params: config };

  return _axios.post(url, data, config);
};

export const put = (APIorUrl, data = {}) => {
  const url = APIorUrl.path || APIorUrl;
  return _axios.put(url, data);
};

export const _delete = APIorUrl => {
  const url = APIorUrl.path || APIorUrl;
  return _axios.delete(url);
};

export const getPDF = (APIorUrl, params) => {
  return getBlob(APIorUrl, params, "application/pdf");
};

export const getCSV = (APIorUrl, params) => {
  return getBlob(APIorUrl, params, "text/csv");
};

export const getJson = (APIorUrl, params) => {
  return getBlob(APIorUrl, params, "application/json");
};

export const getBlob = (APIorUrl, params = {}, contentType) => {
  const url = APIorUrl.path || APIorUrl;
  return _axios.get(url, {
    params,
    responseType: "blob",
    headers: {
      Accept: contentType
    }
  });
};

export const getDownloadFilename = response => {
  return decodeURI(
    response.headers["content-disposition"].match(/filename="?([^"]+)"?/)[1]
  );
};
